import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

const EASE = [0.645, 0.045, 0.355, 1];

const Page = ({ src, side }) => (
  <div className={`absolute inset-y-0 w-1/2 overflow-hidden bg-white ${side === "l" ? "left-0" : "right-0"}`}>
    {src && <img src={src} alt="" className="h-full w-full object-cover" draggable={false} />}
    <div className={`pointer-events-none absolute inset-y-0 w-10 ${side === "l" ? "right-0 bg-gradient-to-l" : "left-0 bg-gradient-to-r"} from-black/25 to-transparent`} />
  </div>
);

const Face = ({ src, back, side }) => (
  <div className="absolute inset-0 overflow-hidden bg-white [backface-visibility:hidden]" style={back ? { transform: "rotateY(180deg)" } : undefined}>
    {src && <img src={src} alt="" className="h-full w-full object-cover" draggable={false} />}
    <div className={`pointer-events-none absolute inset-y-0 w-10 ${side === "l" ? "right-0 bg-gradient-to-l" : "left-0 bg-gradient-to-r"} from-black/25 to-transparent`} />
  </div>
);

const Btn = ({ onClick, disabled, label, children, testid }) => (
  <button onClick={onClick} disabled={disabled} aria-label={label} data-testid={testid} className="flex h-12 w-12 items-center justify-center rounded-full border border-stone-600 text-paper transition-colors duration-300 hover:border-accent hover:bg-accent disabled:pointer-events-none disabled:opacity-30">
    {children}
  </button>
);

const useIsMobile = () => {
  const q = "(max-width: 767px)";
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return m;
};

const Spreads = ({ pages }) => {
  const spreads = [[null, pages[0]]];
  for (let i = 1; i < pages.length; i += 2) spreads.push([pages[i], pages[i + 1] ?? null]);
  const [s, setS] = useState(0);
  const [flip, setFlip] = useState(null);
  const go = useCallback((d) => {
    if (flip) return;
    const t = s + d;
    if (t < 0 || t >= spreads.length) return;
    setFlip({ dir: d, to: t });
  }, [s, flip, spreads.length]);
  useEffect(() => {
    const k = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [go]);

  const cur = spreads[s];
  const nxt = flip ? spreads[flip.to] : null;
  const under = flip ? (flip.dir > 0 ? [cur[0], nxt[1]] : [nxt[0], cur[1]]) : cur;
  const closed = (flip ? flip.to : s) === 0;
  return (
    <>
      <motion.div
        className="relative aspect-[1.414/1] w-[min(92vw,calc(74vh*1.414))] [perspective:2600px]"
        animate={{ x: closed ? "-25%" : "0%" }}
        transition={{ duration: 0.9, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        data-testid="brochure-book"
      >
        {under[0] && <Page src={under[0]} side="l" />}
        {under[1] && <Page src={under[1]} side="r" />}
        {flip && (
          <motion.div
            key={`${s}-${flip.to}`}
            className={`absolute inset-y-0 w-1/2 shadow-2xl [transform-style:preserve-3d] ${flip.dir > 0 ? "right-0 origin-left" : "left-0 origin-right"}`}
            initial={{ rotateY: 0 }}
            animate={{ rotateY: flip.dir > 0 ? -180 : 180 }}
            transition={{ duration: 1, ease: EASE }}
            onAnimationComplete={() => { setS(flip.to); setFlip(null); }}
          >
            <Face src={flip.dir > 0 ? cur[1] : cur[0]} side={flip.dir > 0 ? "r" : "l"} />
            <Face src={flip.dir > 0 ? nxt[0] : nxt[1]} side={flip.dir > 0 ? "l" : "r"} back />
          </motion.div>
        )}
        {!flip && cur[1] && <button className="absolute inset-y-0 right-0 w-1/2 cursor-e-resize" onClick={() => go(1)} aria-label="Turn page" data-testid="brochure-page-right" disabled={s === spreads.length - 1} />}
        {!flip && cur[0] && <button className="absolute inset-y-0 left-0 w-1/2 cursor-w-resize" onClick={() => go(-1)} aria-label="Turn page back" data-testid="brochure-page-left" />}
      </motion.div>
      <Controls label={s === 0 ? "Cover" : `Pages ${s * 2} — ${Math.min(s * 2 + 1, pages.length)}`} prev={() => go(-1)} next={() => go(1)} atStart={s === 0} atEnd={s === spreads.length - 1} />
    </>
  );
};

const Single = ({ pages }) => {
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const go = (d) => { const t = i + d; if (t >= 0 && t < pages.length) { setDir(d); setI(t); } };
  return (
    <>
      <div className="relative aspect-[0.707/1] w-[min(86vw,calc(68vh*0.707))] [perspective:1800px]" onClick={(e) => e.stopPropagation()} data-testid="brochure-book">
        <motion.img
          key={i}
          src={pages[i]}
          alt=""
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          onDragEnd={(_, info) => { if (info.offset.x < -50) go(1); else if (info.offset.x > 50) go(-1); }}
          className="absolute inset-0 h-full w-full bg-white object-cover shadow-2xl"
          style={{ transformOrigin: dir > 0 ? "left center" : "right center" }}
          initial={{ rotateY: dir > 0 ? 70 : -70, opacity: 0.4 }}
          animate={{ rotateY: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: EASE }}
        />
      </div>
      <Controls label={`Page ${i + 1} / ${pages.length}`} prev={() => go(-1)} next={() => go(1)} atStart={i === 0} atEnd={i === pages.length - 1} />
    </>
  );
};

const Controls = ({ label, prev, next, atStart, atEnd }) => (
  <div className="mt-6 flex items-center gap-5 md:mt-8" onClick={(e) => e.stopPropagation()}>
    <Btn onClick={prev} disabled={atStart} label="Previous page" testid="brochure-prev-button"><ArrowLeft size={18} /></Btn>
    <span className="min-w-[8rem] text-center font-mono text-[11px] uppercase tracking-[0.18em] text-stone-300" data-testid="brochure-page-label">{label}</span>
    <Btn onClick={next} disabled={atEnd} label="Next page" testid="brochure-next-button"><ArrowRight size={18} /></Btn>
  </div>
);

export const Brochure = ({ pages }) => {
  const mobile = useIsMobile();
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 pb-6">
      {mobile ? <Single pages={pages} /> : <Spreads pages={pages} />}
    </div>
  );
};
